/**
 * Help Articles Management - Main Component (Refactored)
 * Split into: ArticleCard, useHelpArticles
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Book, Plus, FileText, Clock, RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Import sub-components
import { useHelpArticles } from './help/useHelpArticles';
import { ArticleCard } from './help/ArticleCard';

const HelpArticlesManagement = () => {
  const { isDark } = useTheme();
  const {
    sections,
    versionLogs,
    loading,
    editingId,
    editForm,
    setEditForm,
    startEdit,
    cancelEdit,
    saveArticle,
    createArticle,
    deleteArticle,
    togglePublish,
    reorderArticles,
    fetchArticles
  } = useHelpArticles();

  const [activeTab, setActiveTab] = useState('articles');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    status: 'draft',
    tags: []
  });

  const handleCreate = async () => {
    const success = await createArticle(newArticle);
    if (success) {
      setNewArticle({ title: '', content: '', status: 'draft', tags: [] });
      setShowCreateDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Book className="w-6 h-6 text-yellow-400" />
            Help Articles
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage help content shown in Settings → Help
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-green-400 border-green-400/50">
            {sections.filter(s => s.status === 'published').length} published
          </Badge>
          <Badge variant="outline" className="text-gray-400 border-gray-400/50">
            {sections.filter(s => s.status === 'draft').length} drafts
          </Badge>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black">
                <Plus className="w-4 h-4 mr-2" />
                New Article
              </Button>
            </DialogTrigger>
            <DialogContent className={`max-w-lg ${isDark ? 'bg-gray-900 border-gray-700' : ''}`}>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Create New Article</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Title
                  </label>
                  <Input
                    value={newArticle.title}
                    onChange={(e) => setNewArticle(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Article title"
                    className={`mt-1 ${isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}`}
                  />
                </div>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Content
                  </label>
                  <Textarea
                    value={newArticle.content}
                    onChange={(e) => setNewArticle(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Article content (Markdown supported)"
                    rows={8}
                    className={`mt-1 ${isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}`}
                  />
                </div>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tags (comma-separated)
                  </label>
                  <Input
                    value={newArticle.tags.join(', ')}
                    onChange={(e) => setNewArticle(prev => ({ 
                      ...prev, 
                      tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                    }))}
                    placeholder="getting-started, account"
                    className={`mt-1 ${isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}`}
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={!newArticle.title || !newArticle.content}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black"
                >
                  Create Article
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
          <TabsTrigger value="articles" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
            <FileText className="w-4 h-4 mr-2" />
            Articles ({sections.length})
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" />
            Version History
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="mt-4">
          <div className="space-y-4">
            {sections.length === 0 ? (
              <Card className={isDark ? 'bg-gray-800/50 border-gray-700' : ''}>
                <CardContent className="p-8 text-center">
                  <Book className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    No articles yet. Create your first help article!
                  </p>
                </CardContent>
              </Card>
            ) : (
              sections.map((section, index) => (
                <ArticleCard
                  key={section.id}
                  section={section}
                  isEditing={editingId === section.id}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onStartEdit={startEdit}
                  onSave={saveArticle}
                  onCancel={cancelEdit}
                  onDelete={deleteArticle}
                  onTogglePublish={togglePublish}
                  onReorder={reorderArticles}
                  isFirst={index === 0}
                  isLast={index === sections.length - 1}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Version History Tab */}
        <TabsContent value="logs" className="mt-4">
          <Card className={isDark ? 'bg-gray-800/50 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle className={isDark ? 'text-white' : ''}>
                Version History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {versionLogs.length === 0 ? (
                <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No version history yet
                </p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {versionLogs.map((log) => (
                    <div 
                      key={log.id}
                      className={`p-3 rounded-lg ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {log.article_title}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {log.action}
                        </Badge>
                      </div>
                      <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {log.admin_name} • {new Date(log.timestamp).toLocaleString()}
                      </div>
                      {log.changes && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {log.changes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HelpArticlesManagement;
